<template>
    <div class="login-container">
        <div class="form-side">
            <div class="login-box" v-if="showTwoFactor && !showCompanySelection">
                <div class="logo">
                    <img src="@/assets/logo.png" alt="Logo Weeduc" />
                </div>
                <TwoFactorStep
                    variant="portal"
                    :email="twoFactorEmail"
                    :method="twoFactorMethod"
                    :otpauth-uri="otpauthUri"
                    :busy="twoFactorBusy"
                    :error="twoFactorError"
                    :resent="twoFactorResent"
                    @continue="continueAfterTwoFactor"
                    @choose="chooseTwoFactorMethod"
                    @resend="resendTwoFactorCode"
                    @back="cancelTwoFactor"
                />
            </div>

            <div class="login-box" v-if="emailLinkValidating && !showCompanySelection && !showTwoFactor">
                <div class="logo">
                    <img src="@/assets/logo.png" alt="Logo Weeduc" />
                </div>
                <p class="email-link-status">{{ $t('auth.emailLinkValidating') }}</p>
            </div>

            <div class="login-box" v-if="showEmailLink && !showCompanySelection && !showTwoFactor && !emailLinkValidating">
                <div class="logo">
                    <img src="@/assets/logo.png" alt="Logo Weeduc" />
                </div>
                <form @submit.prevent="sendEmailLink">
                    <h3 class="email-link-title">{{ $t('auth.emailLinkTitle') }}</h3>
                    <p class="email-link-lead">{{ $t('auth.emailLinkSubtitle') }}</p>
                    <div class="form-group">
                        <label>{{ $t('auth.email') }}</label>
                        <input
                            type="email"
                            v-model="email"
                            :placeholder="$t('auth.emailPlaceholder')"
                            required
                            :disabled="emailLinkBusy"
                        />
                    </div>
                    <div v-if="emailLinkSent" class="login-success">
                        {{ $t('auth.emailLinkSent') }}
                    </div>
                    <div v-if="emailLinkError" class="login-error">
                        {{ emailLinkError }}
                    </div>
                    <button type="submit" class="btn-login" :disabled="emailLinkBusy">
                        {{ emailLinkBusy ? $t('auth.emailLinkSending') : $t('auth.emailLinkSend') }}
                    </button>
                    <button type="button" class="btn-email-link" @click="backToPasswordLogin">
                        {{ $t('auth.emailLinkBack') }}
                    </button>
                    <RecaptchaNotice />
                </form>
            </div>

            <!-- Primeira tela de login -->
            <div class="login-box" v-if="!showCompanySelection && !showTwoFactor && !showEmailLink && !emailLinkValidating">
                <div class="logo">
                    <img src="@/assets/logo.png" alt="Logo Weeduc" />
                </div>
                <form @submit.prevent="handleLogin">
                    <div class="form-group">
                        <label>{{ $t('auth.email') }}</label>
                        <input 
                            type="email"
                            v-model="email"
                            :placeholder="$t('auth.emailPlaceholder')"
                            required
                        />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('auth.password') }}</label>
                        <div class="password-input">
                            <input 
                                :type="showPassword ? 'text' : 'password'"
                                v-model="password"
                                :placeholder="$t('auth.passwordPlaceholder')"
                                required
                            />
                            <span class="password-toggle" @click="togglePassword">
                                <fa :icon="showPassword ? 'eye-slash' : 'eye'" />
                            </span>
                        </div>
                    </div>
                    <div v-if="loginError" class="login-error">
                        <template v-if="rateLimited">{{ $t('auth.tooManyAttempts') }}</template>
                        <template v-else>{{ $t('auth.incorrectCredentials') }} <a href="/esqueci-senha-portal">{{ $t('auth.resetYourPassword') }}</a>.</template>
                    </div>
                    <div v-if="emailLinkError" class="login-error">
                        {{ emailLinkError }}
                    </div>
                    <div class="links">
                        <a href="/esqueci-senha-portal">{{ $t('auth.forgotPassword') }}</a>
                    </div>
                    <button type="submit" class="btn-login">{{ $t('auth.login') }}</button>
                    <div class="login-divider"><span>{{ $t('common.or') }}</span></div>
                    <button type="button" class="btn-email-link" @click="openEmailLink">
                        {{ $t('auth.loginWithEmailLink') }}
                    </button>
                    <RecaptchaNotice />
                </form>
            </div>

            <!-- Segunda tela - Seleção de empresa -->
            <div class="login-box" v-if="showCompanySelection">
                <div class="logo">
                    <img src="@/assets/logo.png" alt="Logo Weeduc" />
                </div>
                <div class="company-selection">
                    <h3>{{ $t('auth.selectACompany') }}</h3>
                    <p>{{ $t('auth.multipleCompanies') }}</p>
                    
                    <div class="form-group">
                        <label>{{ $t('auth.company') }}</label>
                        <select v-model="selectedCompany" required>
                            <option value="0">{{ $t('auth.selectACompany') }}</option>
                            <option 
                                v-for="website in availableWebsites" 
                                :key="website.id" 
                                :value="website"
                            >
                                {{ website.nome }}
                            </option>
                        </select>
                    </div>

                    <div v-if="loginError" class="login-error">
                        <template v-if="rateLimited">{{ $t('auth.tooManyAttempts') }}</template>
                        <template v-else>{{ $t('auth.incorrectCredentials') }} <a href="/esqueci-senha-portal">{{ $t('auth.resetYourPassword') }}</a>.</template>
                    </div>
                    <div class="company-actions">
                        <button type="button" class="btn-back" @click="goBackToLogin">
                            {{ $t('common.back') }}
                        </button>
                        <button 
                            type="button" 
                            class="btn-login" 
                            @click="proceedWithSelectedCompany"
                            :disabled="!selectedCompany || selectedCompany === 0"
                        >
                            {{ $t('auth.login') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="login-version">
                <AppVersionLabel product="weeduc" />
            </div>
        </div>
        <div class="banner-side">
            <div class="banner-content">
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import userApi from '@/utils/user.js';
import loginApi from '@/utils/login.js';
import { syncAdminModeFromTypeBusiness } from '@/utils/corporate/adminDashboardMode';
import { getRecaptchaToken } from '@/utils/recaptcha';
import RecaptchaNotice from '@/components/RecaptchaNotice.vue';
import TwoFactorStep from '@/components/Auth/TwoFactorStep.vue';
import AppVersionLabel from '@/components/AppVersionLabel.vue';
import { isRateLimited } from '@/utils/rateLimit';
import { isPortalLayoutDark } from '@/utils/portalLayoutTheme';

export default {
    components: { RecaptchaNotice, TwoFactorStep, AppVersionLabel },
    data() {
        return {
            email: '',
            password: '',
            token: '',
            refreshToken: '',
            typeIdLocal: null,
            idUser: null,
            websites: null,
            darkMode: false,
            typeSystemLocal: null,
            lististes: [],
            showPassword: false,
            loginError: false,
            rateLimited: false,
            showCompanySelection: false,
            availableWebsites: [],
            selectedCompany: 0,
            loginResponse: null,
            showTwoFactor: false,
            challengeToken: '',
            twoFactorEmail: '',
            twoFactorMethod: 'email',
            otpauthUri: '',
            twoFactorError: '',
            twoFactorBusy: false,
            twoFactorResent: false,
            pendingWebsite: null,
            sessionAccessToken: '',
            showEmailLink: false,
            emailLinkBusy: false,
            emailLinkSent: false,
            emailLinkError: '',
            emailLinkValidating: false,
        }
    },
    created() {
        this.consumeEmailLinkFromRoute(this.$route.query.token);
    },
    watch: {
        '$route.query.token'(linkToken) {
            this.consumeEmailLinkFromRoute(linkToken);
        },
    },
    methods: {
        ...mapActions([
            'storeTypeSystem',
            'toggleDarkMode',
            'clearPortalDarkManual', 
            'storeToken',
            'storeRefreshToken',
            'storeTypeId',
            'storeWebsites',
            'storeUser',
        ]),
        togglePassword() {
            this.showPassword = !this.showPassword;
        },
        openEmailLink() {
            this.showEmailLink = true;
            this.loginError = false;
            this.rateLimited = false;
            this.emailLinkError = '';
            this.emailLinkSent = false;
            this.password = '';
        },
        backToPasswordLogin() {
            this.showEmailLink = false;
            this.emailLinkError = '';
            this.emailLinkSent = false;
            this.emailLinkBusy = false;
        },
        async sendEmailLink() {
            this.emailLinkBusy = true;
            this.emailLinkError = '';
            this.emailLinkSent = false;
            this.rateLimited = false;
            try {
                const recaptchaToken = await getRecaptchaToken(this, 'login_email_link');
                if (!recaptchaToken) {
                    this.emailLinkError = this.$t('auth.emailLinkSendError');
                    return;
                }
                await loginApi.requestEmailLink({
                    email: this.email,
                    recaptchaToken,
                    redirectUrl: `${window.location.origin}/portal`,
                });
                this.emailLinkSent = true;
            } catch (error) {
                this.rateLimited = isRateLimited(error);
                this.emailLinkError = this.rateLimited
                    ? this.$t('auth.tooManyAttempts')
                    : this.$t('auth.emailLinkSendError');
                console.error(error);
            } finally {
                this.emailLinkBusy = false;
            }
        },
        consumeEmailLinkFromRoute(linkToken) {
            if (typeof linkToken !== 'string' || !linkToken.trim() || this.emailLinkValidating) {
                return;
            }
            this.consumeEmailLink(linkToken.trim());
        },
        async consumeEmailLink(linkToken) {
            this.emailLinkValidating = true;
            this.showEmailLink = false;
            this.loginError = false;
            this.emailLinkError = '';
            this.rateLimited = false;
            let authenticated = false;
            try {
                if (this.$route.query.token) {
                    await this.$router.replace({ path: '/portal', query: {} });
                }
                const response = await loginApi.validateEmailLink(linkToken);
                const data = response?.data;
                if (loginApi.isTwoFactorChallenge(data)) {
                    this.openTwoFactorChallenge(data);
                    return;
                }
                const sites = data?.listWebsite || [];
                if (sites.length > 1 && data?.acessToken) {
                    await this.handlePortalAuth(data);
                    return;
                }
                if (data?.acessToken) {
                    authenticated = true;
                    if (sites.length >= 1) {
                        this.websites = sites[0];
                    }
                    await this.completeLogin(data);
                    return;
                }
                this.emailLinkError = this.$t('auth.emailLinkInvalid');
            } catch (error) {
                authenticated = false;
                this.rateLimited = isRateLimited(error);
                this.emailLinkError = this.rateLimited
                    ? this.$t('auth.tooManyAttempts')
                    : this.$t('auth.emailLinkInvalid');
                console.error(error);
            } finally {
                if (!authenticated) {
                    this.emailLinkValidating = false;
                }
            }
        },
        async handleLogin() {
            this.loginError = false;
            this.rateLimited = false;
            this.showTwoFactor = false;
            this.checkUserCompanies();
        },
        markLoginError(error) {
            this.rateLimited = isRateLimited(error);
            this.loginError = true;
        },
        openTwoFactorChallenge(data, website) {
            this.challengeToken = data.challengeToken;
            this.twoFactorEmail = data.twoFactorEmail || this.email;
            this.twoFactorMethod = data.twoFactorMethod || 'email';
            this.otpauthUri = data.otpauthUri || '';
            this.twoFactorError = '';
            this.twoFactorResent = false;
            this.pendingWebsite = website || this.pendingWebsite;
            this.showTwoFactor = true;
        },
        async chooseTwoFactorMethod(method) {
            this.twoFactorBusy = true;
            this.twoFactorError = '';
            this.twoFactorResent = false;
            try {
                const response = await loginApi.chooseTwoFactorMethod({
                    challengeToken: this.challengeToken,
                    method,
                });
                this.openTwoFactorChallenge(response.data);
            } catch (error) {
                this.twoFactorError = isRateLimited(error)
                    ? this.$t('auth.tooManyAttempts')
                    : this.$t('auth.twoFactorInvalid');
            } finally {
                this.twoFactorBusy = false;
            }
        },
        async continueAfterTwoFactor(code) {
            this.twoFactorBusy = true;
            this.twoFactorError = '';
            this.twoFactorResent = false;
            try {
                const response = await loginApi.verifyTwoFactor({
                    challengeToken: this.challengeToken,
                    code,
                });
                const website = this.pendingWebsite;
                this.showTwoFactor = false;
                this.pendingWebsite = null;
                if (website) {
                    this.websites = website;
                    await this.completeLogin(response.data);
                    return;
                }
                await this.handlePortalAuth(response.data);
            } catch (error) {
                this.twoFactorError = isRateLimited(error)
                    ? this.$t('auth.tooManyAttempts')
                    : this.$t('auth.twoFactorInvalid');
                console.error(error);
            } finally {
                this.twoFactorBusy = false;
            }
        },
        async resendTwoFactorCode() {
            this.twoFactorBusy = true;
            this.twoFactorError = '';
            this.twoFactorResent = false;
            try {
                const response = await loginApi.resendTwoFactor(this.challengeToken);
                if (response?.data?.challengeToken) {
                    this.challengeToken = response.data.challengeToken;
                }
                this.twoFactorResent = true;
            } catch (error) {
                this.twoFactorError = isRateLimited(error)
                    ? this.$t('auth.tooManyAttempts')
                    : this.$t('auth.twoFactorInvalid');
            } finally {
                this.twoFactorBusy = false;
            }
        },
        cancelTwoFactor() {
            this.showTwoFactor = false;
            this.challengeToken = '';
            this.twoFactorMethod = 'email';
            this.otpauthUri = '';
            this.pendingWebsite = null;
            this.twoFactorError = '';
            this.twoFactorResent = false;
        },
        async captchaToken() {
            const recaptchaToken = await getRecaptchaToken(this, 'login_student');
            if (!recaptchaToken) {
                this.loginError = true;
                return null;
            }
            return recaptchaToken;
        },
        async checkUserCompanies() {
            try {
                const recaptchaToken = await this.captchaToken();
                if (!recaptchaToken) {
                    return;
                }
                // Primeira chamada sem domain para verificar se há múltiplas empresas
                const response = await loginApi.token({
                    "email": this.email,
                    "password": this.password,
                    recaptchaToken,
                }, {
                    withCredentials: false,
                });

                if (loginApi.isTwoFactorChallenge(response.data)) {
                    this.openTwoFactorChallenge(response.data);
                    return;
                }
                await this.handlePortalAuth(response.data);
            } catch (error) {
                console.error(error,'Erro ao buscar token');
                this.markLoginError(error);
            }
        },
        async handlePortalAuth(data) {
            this.loginResponse = data;
            if (data?.acessToken) {
                this.sessionAccessToken = data.acessToken;
            }
            const sites = data?.listWebsite || [];
            if (sites.length > 1) {
                this.availableWebsites = sites;
                this.showCompanySelection = true;
                return;
            }
            if (sites.length === 1) {
                await this.loginWithCompanyUrl(sites[0]);
                return;
            }
            let domainUrl = window.location.origin + window.location.pathname.split('/portal')[0];
            if (window.location.hostname === 'localhost') {
                domainUrl = process.env.VUE_APP_BASE_URL_HOMOLOGATION;
            }
            await this.loginWithCompanyUrl({ url: domainUrl });
        },
        async loginWithCompanyUrl(selectedWebsite) {
            try {
                if (this.sessionAccessToken && selectedWebsite?.url) {
                    const response = await loginApi.redirectCompany(
                        this.sessionAccessToken,
                        selectedWebsite.url
                    );
                    this.websites = selectedWebsite;
                    await this.completeLogin(response.data);
                    return;
                }
                const recaptchaToken = await this.captchaToken();
                if (!recaptchaToken) {
                    return;
                }
                const response = await loginApi.token({
                    email: this.email,
                    password: this.password,
                    domain: selectedWebsite.url,
                    recaptchaToken,
                }, {
                    withCredentials: false,
                });
                if (loginApi.isTwoFactorChallenge(response.data)) {
                    this.openTwoFactorChallenge(response.data, selectedWebsite);
                    return;
                }
                this.websites = selectedWebsite;
                await this.completeLogin(response.data);
            } catch (error) {
                console.error(error,'Erro ao fazer login com empresa selecionada');
                this.markLoginError(error);
            }
        },
        async proceedWithSelectedCompany() {
            if (!this.selectedCompany || this.selectedCompany === 0) return;

            await this.loginWithCompanyUrl(this.selectedCompany);
        },
        persistLoginWebsite(website) {
            this.websites = website || null;
            this.storeWebsites({ websites: this.websites });
            // typeBusiness 2 → corporativo; 1 → escolar (sobrescreve company persistida)
            syncAdminModeFromTypeBusiness(this.websites);
        },
        async completeLogin(loginData) {
            this.token = loginData.acessToken;
            this.typeIdLocal = loginData.type;
            this.refreshToken = loginData.refreshToken;
            this.idUser = loginData.user.id;
            if (!this.websites && loginData.listWebsite && loginData.listWebsite[0]) {
                this.websites = loginData.listWebsite[0];
            }
            this.darkMode = isPortalLayoutDark(this.websites && this.websites.typeLayoutPortal) === true;
            this.typeSystemLocal = loginData.typeSystem;

            this.storeTypeSystem({typeSystem: loginData.typeSystem});
            this.clearPortalDarkManual();
            this.toggleDarkMode({dark: this.darkMode});
            this.setItemStorage();
            this.storeToken({token: this.token})
            this.storeRefreshToken({refreshToken: this.refreshToken})
            this.storeTypeId({typeId: this.typeIdLocal})
            this.fetchUserData(this.idUser, this.token);
            this.persistLoginWebsite(this.websites);
            await this.getLogo();
            this.$router.push('/aluno/dashboard');
        },
        goBackToLogin() {
            this.showCompanySelection = false;
            this.selectedCompany = 0;
            this.availableWebsites = [];
            this.loginResponse = null;
            this.showTwoFactor = false;
        },
        async getTokenManyCompany() {
            let domainUrl = window.location.origin + window.location.pathname.split('/portal')[0];
            if (window.location.hostname === 'localhost') {
                domainUrl = process.env.VUE_APP_BASE_URL_HOMOLOGATION;
            }
            await this.loginWithCompanyUrl({ url: domainUrl });
        },
        setItemStorage() {
            localStorage.setItem('logado', 'true');
        },
        async fetchUserData(id, tokenAPI) {
            try {
                const userData = await userApi.getDataUserPortal(tokenAPI);
                this.storeUser({user: userData});
            } catch (error) {
                console.error(error,'Erro ao buscar dados do usuário');
            }
        },
        async getLogo() {
            if(window.location.hostname == 'localhost'){
                this.logo = 'https://image.weeduc.com/logo.png';
            }else{
                const aux = window.location.origin.replace(/\/$/, '');
                const url = aux.split('/portal')[0];
                const obj = { url: url };

                try {
                    const response = await loginApi.logo(obj);
                    this.logo = 'https://image.weeduc.com/'+response.logo;
                    if (!this.token) {
                        this.typeSystemLocal = response.typeSystem;
                    }
                    
                } catch (error) {
                    console.error(error,'Erro ao buscar logo');
                }
            }
        }
    }
}
</script>

<style scoped>
.login-container {
    display: flex;
    min-height: 100vh;
}

.banner-side {
    flex: 1 1 0;
    min-width: 0;
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    position: relative;
}

.banner-content {
    position: relative;
    text-align: center;
    padding: 1.25rem;
}

.banner-content h1 {
    font-size: 2.5em;
    margin-bottom: 0.9375rem;
}

.form-side {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #ffffff;
    padding: 1.5rem 1.5rem 1rem;
    box-sizing: border-box;
    flex: 0 0 30rem;
    width: 30rem;
    height: 100vh;
    max-height: 100vh;
    overflow-y: auto;
    position: relative;
}

.login-version {
    position: sticky;
    bottom: 0;
    flex-shrink: 0;
    width: 100%;
    margin-top: 12px;
    padding-top: 8px;
    text-align: center;
    color: #6b7280;
    background: #ffffff;
}

.login-box {
    width: 100%;
    max-width: 25rem;
    background: white;
    padding: 1.75rem 2.5rem 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 0.125rem 0.625rem rgba(0,0,0,0.1);
    margin-top: auto;
    margin-bottom: auto;
}

.logo {
    text-align: center;
    margin-bottom: 1.25rem;
}

.logo img {
    width: 12.5rem;
}

h2 {
    text-align: center;
    color: #272f3a;
    margin-bottom: 1.875rem;
}

.company-selection {
    text-align: center;
}

.company-selection h3 {
    color: #272f3a;
    margin-bottom: 1rem;
    font-size: 1.5rem;
}

.company-selection p {
    color: #757e8a;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    line-height: 1.4;
}

.form-group {
    margin-bottom: 1.25rem;
}

label {
    display: block;
    margin-bottom: 0.5rem;
    color: #757e8a;
    text-align: left;
}

input, select {
    width: 100%;
    padding: 0.75rem;
    border: 0.0625rem solid #ddd;
    border-radius: 0.25rem;
    font-size: 0.875rem;
}

select {
    background-color: white;
    cursor: pointer;
}

.password-input {
    position: relative;
    display: flex;
    align-items: center;
}

.password-toggle {
    position: absolute;
    right: 0.75rem;
    cursor: pointer;
    color: #757e8a;
}

.login-error {
    color: #d32f2f;
    background: #fff0f0;
    border: 1px solid #d32f2f;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 10px;
    font-size: 0.95em;
    margin: 15px;
    text-align: center;
}

.login-error a {
    color: #3db8bf;
    text-decoration: underline;
}

.login-success {
    color: #1b7f4a;
    background: #f1fbf5;
    border: 1px solid #1b7f4a;
    border-radius: 4px;
    padding: 10px;
    margin: 15px;
    font-size: 0.95em;
    text-align: center;
}

.email-link-title {
    text-align: center;
    color: #272f3a;
    margin: 0 0 0.5rem;
    font-size: 1.35rem;
}

.email-link-lead,
.email-link-status {
    color: #757e8a;
    text-align: center;
    font-size: 0.9rem;
    line-height: 1.4;
    margin: 0 0 1.25rem;
}

.login-divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 4px 16px 0;
    color: #757e8a;
    font-size: 0.8rem;
}

.login-divider::before,
.login-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e3e6ea;
}

.btn-email-link {
    width: 93%;
    padding: 0.75rem;
    background: #fff;
    color: #3db8bf;
    border: 1px solid #3db8bf;
    border-radius: 0.25rem;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s, color 0.3s;
    margin: 10px;
}

.btn-email-link:hover {
    background: #f3fbfc;
}

.btn-login {
    width: 93%;
    padding: 0.75rem;
    background-color: #3db8bf;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
    margin: 10px;
}

.btn-login:hover {
    background-color: #2a9ca3;
}

.btn-login:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.btn-back {
    width: 40%;
    padding: 0.75rem;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
    margin-right: 10px;
}

.btn-back:hover {
    background-color: #5a6268;
}

.company-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1.5rem;
}

.company-actions .btn-login {
    width: 55%;
    margin: 0;
}

.links {
    margin-top: 10px;
    text-align: center;
    display: flex;
    justify-content: end;
    padding: 0px 13px;
}

.links a {
    color: #3db8bf;
    text-decoration: none;
    font-size: 12px;
}

.links a:hover {
    color: #333;
}

.login-box :deep(.recaptcha-notice) {
    margin: 8px 16px 4px;
}

@media (max-width: 48rem) {
    .login-container {
        flex-direction: column;
    }
    
    .banner-side {
        display: none;
    }
    
    .form-side {
        padding: 1.25rem 1.25rem 3rem;
    }
    
    .login-box {
        padding: 1.25rem;
    }

    .company-actions {
        flex-direction: column;
        gap: 10px;
    }

    .company-actions .btn-login,
    .btn-back {
        width: 100%;
        margin: 0;
    }
}
.banner-side {
    background-image: url('@/assets/portal/Login-weeduc.png');
    background-size: cover;
    background-position: center;
    background-color: none!important;
}
@media (max-width: 768px) {
    .form-side {
        flex: 1 1 auto;
        width: 100%;
        height: 100vh;
    }
}
</style>
